import styled from 'styled-components';

import ClickEventLogger from '@/components/common/ClickEventLogger';
import LinkTag from '@/components/common/LinkTag';
import HospitalListItem from '@/components/hospital/HospitalListItem/HospitalListItem';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import {
  EmptyResult,
  RequestInfoBox,
  ResultSection,
  SearchMagazineItem,
  SearchQuestionItem,
} from '@/components/templates/mobile/home/HomeSearchTemplate/components';
import Doctor from '@/components/templates/mobile/home/HomeSearchTemplate/components/SearchResult/Doctor/Doctor';
import { SearchDoctorViewModel } from '@/hooks/search/doctor/useSearchDoctorViewModel';
import { HomeSearchViewModel } from '@/hooks/search/useHomeSearchViewModel';
import useReportModal from '@/hooks/useReportModal';
import theme from '@/styles/theme';
import { ROUTES } from '@/types/common';
import { ContractType } from '@/types/hospital';
import { IntegratedSearchTabType, RequestButtonType } from '@/types/search';
import FeatureByKeyword from '../component/FeatureByKeyword';
import IntegratedSearchResultSkeleton from '../component/IntegratedSearchResultSkeleton';
import SnippetByKeyword from '../component/SnippetByKeyword';

const getSearchResultTemplateItemStyle = (isLast: boolean) => {
  return {
    margin: '24px 24px 0px',
    padding: '0px 0px 24px',
    borderBottom: isLast ? 'none' : `1px solid ${theme.color.gray40}`,
  };
};

const IntegratedSearchResult = ({
  viewModel,
  doctorViewModel,
}: {
  viewModel: HomeSearchViewModel;
  doctorViewModel: SearchDoctorViewModel;
}) => {
  const { showReportModal, onCloseReportModal, reportItems } = useReportModal();

  if (viewModel.isIntegratedSearchLoading) {
    return <IntegratedSearchResultSkeleton />;
  }

  if (viewModel.hasNoData) {
    return <EmptyResult handleClickRequestButton={viewModel.handleClickRequestButton} />;
  }

  return (
    <>
      <SnippetByKeyword
        keyword={viewModel.generalSearchMetaByKeyword?.keyword ?? ''}
        snippet={viewModel.generalSearchMetaByKeyword?.snippet}
        navigateToDoctorList={viewModel.navigateToDoctorList}
      />
      <FeatureByKeyword
        features={viewModel.generalSearchMetaByKeyword?.features}
        navigateToFindPharmacyScreen={viewModel.navigateToFindPharmacyScreen}
        navigateDietInjectionScreen={viewModel.navigateDietInjectionScreen}
        navigateToEmergencyContraceptiveScreen={viewModel.navigateToEmergencyContraceptiveScreen}
      />
      <ResultSection title='비대면 진료' hasResultData={!!viewModel.doctors.length}>
        <ResultSection.Result>
          <DoctorResultWrapper>
            {viewModel.doctors?.map((doctor) => (
              <Doctor
                onClick={() => doctorViewModel.onClickDoctor(doctor.publicId)}
                key={`search-result-doctor-${doctor.id}`}
                doctor={doctor}
              />
            ))}
          </DoctorResultWrapper>
        </ResultSection.Result>
        {viewModel.hasMoreDoctors && (
          <ResultSection.MoreButton
            style={{ marginTop: 0 }}
            onClick={() => viewModel.onChangeTabByMoreData(IntegratedSearchTabType.Doctor)}
          />
        )}
      </ResultSection>
      <ResultSection title={'가까운 병원 찾기'} hasResultData={!!viewModel.keywordSearchHospital?.elements.length}>
        <ResultSection.ResultAddress
          address={viewModel.getHeaderAddress()}
          getCurrentLocation={viewModel.requestGeolocationPermission}
        />
        <ResultSection.ResultWrapper>
          <HospitalResultWrapper>
            {viewModel.keywordSearchHospital?.elements.map((hospital) => (
              <LinkTag
                key={`search_hospital_${hospital.hospitalPublicId}`}
                onClickNavigateToScreen={() => viewModel.goToAppHospital(hospital.hospitalPublicId)}
                href={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
              >
                <ClickEventLogger
                  pageName={'user_web_search'}
                  eventName='search_item_click'
                  payload={{
                    keyword: viewModel.keyword,
                    hospital_id: hospital.hospitalPublicId,
                    tab: IntegratedSearchTabType.All,
                    result_section: IntegratedSearchTabType.Hospital,
                  }}
                >
                  <HospitalListItem
                    name={hospital.hospitalName}
                    distance={hospital.hospitalDistance}
                    rating={hospital.reviewScore}
                    reviewTotalCount={hospital.reviewTotalCount}
                    badges={hospital.hospitalBadges}
                    imageUrl={hospital.hospitalRepresentativeImageUrl}
                    address={hospital.hospitalAddress}
                    status={hospital.hospitalOperationStatus}
                    isBookable={hospital.isBookable}
                    representativeDepartmentName={hospital.hospitalRepresentativeDepartmentName}
                    hospitalPid={hospital.hospitalPublicId}
                    doctorDetailPageHref={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
                    onClickMoveToDoctorDetail={() => {
                      viewModel.goToAppHospital(hospital.hospitalPublicId);
                    }}
                    hasNonF2fDoctors={!!hospital.doctors.find((doctor) => doctor.contractType === ContractType.All)}
                  />
                </ClickEventLogger>
              </LinkTag>
            ))}
          </HospitalResultWrapper>
        </ResultSection.ResultWrapper>
        {viewModel.keywordSearchHospital?.hasMore && (
          <ResultSection.MoreButton
            style={{ marginTop: 0 }}
            onClick={() => viewModel.onChangeTabByMoreData(IntegratedSearchTabType.Hospital)}
          />
        )}
      </ResultSection>
      <ResultSection title={'실시간 의료상담'} hasResultData={!!viewModel.keywordSearchQna?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchQna?.elements.map((question) => {
            return (
              <SearchQuestionItem
                key={question.question.questionPid}
                questionCard={question}
                onClickQuestion={viewModel.handleClickQuestion}
              />
            );
          })}
        </ResultSection.Result>
        {viewModel.keywordSearchQna?.hasMore && (
          <ResultSection.MoreButton onClick={() => viewModel.onChangeTabByMoreData(IntegratedSearchTabType.Qna)} />
        )}
      </ResultSection>
      <ResultSection title={'건강 매거진'} hasResultData={!!viewModel.keywordSearchMagazine?.elements.length}>
        <ResultSection.Result>
          {viewModel.keywordSearchMagazine?.elements.map((magazine, index) => (
            <SearchMagazineItem
              key={magazine.magazinePid}
              magazine={magazine}
              onClickMagazine={() => viewModel.handleClickMagazine(magazine)}
              pageName={'mobile_web_search'}
              containerStyle={getSearchResultTemplateItemStyle(
                viewModel.keywordSearchMagazine ? index === viewModel.keywordSearchMagazine?.elements.length - 1 : false
              )}
            />
          ))}
        </ResultSection.Result>
        {viewModel.keywordSearchMagazine?.hasMore && (
          <ResultSection.MoreButton onClick={() => viewModel.onChangeTabByMoreData(IntegratedSearchTabType.Magazine)} />
        )}
      </ResultSection>
      <RequestInfoBox handleClickRequestButton={() => viewModel.handleClickRequestButton(RequestButtonType.BOTTOM)} />
      {reportItems && (
        <ReportSelectModal isVisible={showReportModal} onClose={onCloseReportModal} selectList={reportItems} />
      )}
    </>
  );
};

export default IntegratedSearchResult;

const DoctorResultWrapper = styled.div`
  > div:not(:last-child) {
    border-bottom-width: 1px;
    border-bottom-color: ${({ theme }) => theme.color.gray40};
    border-bottom-style: solid;
  }
`;

const HospitalResultWrapper = styled.div`
  > *:not(:last-child) > div {
    border-bottom-style: solid;
    border-bottom-width: 1px;
    border-bottom-color: ${({ theme }) => theme.color.gray40};
  }
`;
